<template>
    <div class="employee-counts">
        <div class="employee-counts__header">
            <h4 class="employee-counts__title">Employee Counts</h4>
            <div class="employee-counts__controls">
                <themed-button 
                    v-if="!editing"
                    size="sm" 
                    variant="outline"
                    @click="startEditing"
                >
                    Edit Employee Counts
                </themed-button>
                <template v-else>
                    <themed-button 
                        size="sm" 
                        variant="default"
                        @click="saveEmployeeCounts"
                        :disabled="isSaving"
                    >
                        <theme-icon v-if="isSaving" icon="loader" type="icons" classes="w-4 h-4 animate-spin" />
                        <span>{{ isSaving ? 'Saving...' : 'Save All' }}</span>
                    </themed-button>
                    <themed-button 
                        size="sm" 
                        variant="ghost"
                        @click="cancelEditing"
                        :disabled="isSaving"
                    >
                        Cancel
                    </themed-button>
                </template>
            </div>
        </div>
        
        <themed-table
            ref="employeeTable"
            :columns="dynamicColumns"
            :data="tableData"
            :inline-editable="true"
            edit-mode="bulk"
            :external-edit-mode="editing"
            :pagination="false"
            :searchable="false"
            :sortable="false"
            class="employee-counts__themed-table"
            @update-edit-value="handleCellValueChange"
        />

        <div v-if="showChart" class="employee-counts__chart">
            <theme-area-chart
                :categories="chartCategories"
                :data="chartData"
                :label="'Employee Count'"
                :y-axis-label="'Employees'"
                :format-value="formatChartValue"
            />
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import ThemedButton from '../ThemedButton.vue';
    import ThemeIcon from '../ThemeIcon.vue';
    import ThemeAreaChart from '../charts/ThemeAreaChart.vue';
    
    export default {
        components: {
            ThemedButton,
            ThemeIcon,
            ThemeAreaChart
        },
        props: {
            siteId: {
                type: Number,
                required: true
            },     

            data: {
                type: [Array, Object],
                required: false
            },

            showChart: {
                type: Boolean,
                default: true
            }
        },

        data() {
            return {
                editing: false,
                isSaving: false,
                internalTableData: [],
                localEmployeeCounts: []
            }
        },

        mounted() {
            this.initializeData();
        },

        watch: {
            data: {
                handler(newData) {
                    // Only reinitialize if not in editing mode AND data actually changed
                    if (!this.editing && newData) {
                        console.log('Data prop changed, reinitializing...');
                        this.initializeData();
                    }
                },
                deep: true,
                immediate: false
            }
        },

        methods: {
            initializeData() {
                // Don't reinitialize if we're currently saving
                if (this.isSaving) {
                    console.log('Skipping data initialization - currently saving');
                    return;
                }
                
                console.log('Initializing data...');
                
                // Create a deep copy of employee counts data
                if (this.data) {
                    this.localEmployeeCounts = _.cloneDeep(_.orderBy(this.data, 'year', 'desc'));
                } else {
                    this.localEmployeeCounts = _.cloneDeep(this.defaultYears);
                }

                console.log('Local employee counts initialized:', this.localEmployeeCounts);

                // Initialize table data
                this.initializeTableData();
            },

            initializeTableData() {
                // Don't reinitialize if we're currently saving
                if (this.isSaving) {
                    console.log('Skipping table initialization - currently saving');
                    return;
                }
                
                console.log('Initializing table data from localEmployeeCounts:', this.localEmployeeCounts);
                
                // Create rows for FTE and PSF
                const fteRow = { type: 'FTE' };
                const psfRow = { type: 'PSF' };

                _.forEach(this.localEmployeeCounts, (counts) => {
                    const yearKey = `year_${counts.year}`;
                    fteRow[yearKey] = counts.fte || 0;
                    psfRow[yearKey] = counts.psf || 0;
                });

                this.internalTableData = [fteRow, psfRow];
                console.log('Table data initialized:', this.internalTableData);
            },

            startEditing() {
                this.editing = true;
            },

            handleCellValueChange(field, value, rowIndex, row) {
                // This is called whenever a cell value changes in the table
                console.log(`Cell value changed: ${field} = ${value}, rowIndex: ${rowIndex}, row type: ${row?.type}`);
                
                // Extract year from field name (e.g., "year_2024" -> "2024")
                const yearMatch = field.match(/year_(\d+)/);
                if (!yearMatch) {
                    console.log('Not a year field, ignoring');
                    return;
                }
                
                const year = parseInt(yearMatch[1]);
                const rowType = row?.type; // 'FTE' or 'PSF'
                
                console.log(`Updating ${rowType} for year ${year} to ${value}`);
                
                // Find the year data in localEmployeeCounts
                const yearData = _.find(this.localEmployeeCounts, { year });
                if (!yearData) {
                    console.error(`Year ${year} not found in localEmployeeCounts`);
                    return;
                }
                
                const numValue = parseInt(value) || 0;
                
                // Update the appropriate field
                if (rowType === 'FTE') {
                    console.log(`Setting FTE for year ${year}: ${numValue}`);
                    this.$set(yearData, 'fte', numValue);
                } else if (rowType === 'PSF') {
                    console.log(`Setting PSF for year ${year}: ${numValue}`);
                    this.$set(yearData, 'psf', numValue);
                } else {
                    console.error(`Unknown row type: ${rowType}`);
                }
                
                console.log('Updated localEmployeeCounts:', JSON.parse(JSON.stringify(this.localEmployeeCounts)));
            },

            cancelEditing() {
                this.editing = false;
                // Reinitialize data to original state
                this.initializeData();
            },

            updateLocalDataFromTable() {
                console.log('Final check - localEmployeeCounts should already be updated:', JSON.parse(JSON.stringify(this.localEmployeeCounts)));
                // Data should already be updated via handleCellValueChange
                // This is just a final verification
            },

            saveToBackend(dataToSave) {
                this.isSaving = true;
                
                console.log('Saving to backend:', dataToSave);
                
                axios.post('/sites/' + this.siteId + '/employee-counts', { counts: dataToSave })
                    .then((response) => {
                        console.log('Save response:', response);
                        this.$eventHub.$emit('site-saved', response.data.site);
                        
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });
                        
                        // Update local data with saved values
                        this.localEmployeeCounts = dataToSave;
                        
                        // Don't set editing to false immediately - let it happen after state updates
                        this.$nextTick(() => {
                            this.editing = false;
                        });
                    })
                    .catch((error) => {
                        console.error('Save error:', error);
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response?.data?.message || 'Failed to save employee counts'
                        });
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            },

            formatChartValue(value) {
                return value.toLocaleString();
            },

            saveEmployeeCounts() {
                console.log('Save button clicked');
                console.log('Table ref:', this.$refs.employeeTable);
                console.log('Current localEmployeeCounts BEFORE update:', JSON.parse(JSON.stringify(this.localEmployeeCounts)));
                
                // Get the current table data which has been edited
                this.updateLocalDataFromTable();
                
                console.log('Updated localEmployeeCounts AFTER update:', JSON.parse(JSON.stringify(this.localEmployeeCounts)));
                
                // Make a deep copy to prevent any reference issues
                const dataToSave = JSON.parse(JSON.stringify(this.localEmployeeCounts));
                console.log('Data to save (copy):', dataToSave);
                
                // Save directly to backend with the copy
                this.saveToBackend(dataToSave);
            }
        },

        computed: {
            employeeCounts() {
                if (this.data) {
                    return _.orderBy(this.data, 'year', 'desc');
                }

                return this.defaultYears;
            },

            defaultYears() {
                let years = _.map(_.range(2000, moment().add(1, 'year').year()), (value) => {
                    return {
                        'year': value,
                        'fte': 0,
                        'psf': 0
                    }
                });

                return _.orderBy(years, 'year', 'desc');
            },

            years() {
                return _.map(this.localEmployeeCounts, 'year');
            },

            dynamicColumns() {
                // First column is the row type (FTE, PSF)
                const columns = [
                    {
                        field: 'type',
                        label: 'YEAR',
                        type: 'text',
                        editable: false,
                        width: '80px',
                        align: 'left'
                    }
                ];

                // Add a column for each year from localEmployeeCounts
                _.forEach(this.localEmployeeCounts, (counts) => {
                    columns.push({
                        field: `year_${counts.year}`,
                        label: counts.year.toString(),
                        type: 'number',
                        editable: true,
                        width: '90px',
                        align: 'center',
                        validation: { min: 0 }
                    });
                });

                return columns;
            },

            tableData() {
                // Add unique IDs for row identification
                return this.internalTableData.map((row, index) => ({
                    ...row,
                    id: index + 1 // Add ID for bulk editing
                }));
            },

            chartCategories() {
                return _.map(this.localEmployeeCounts, 'year');
            },

            chartData() {
                return _.map(this.localEmployeeCounts, (row) => {
                    return (parseInt(row.fte) || 0) + (parseInt(row.cte) || 0);
                });
            }
        },
    }
</script>
